import { useEffect, useId, useRef } from 'react';

import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Body copy — plain text or rich nodes (e.g. the cascade message). */
  message: React.ReactNode;
  confirmLabel: string;
  pendingLabel?: string;
  isPending?: boolean;
  /**
   * Block confirming while the body is still resolving what it has to say —
   * distinct from `isPending`, which means the confirmed action is in flight and
   * relabels the button. A dialog whose whole purpose is to show an impact count
   * can otherwise be dismissed with a fast Enter while it still reads
   * "Checking…", which is consent to something the reader never saw.
   */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Reusable confirmation dialog for destructive actions (remove lead, remove team).
 * The confirm button uses Bluebonnet — red is off-brand per the UT brand book, so
 * we convey "destructive" through the confirm affordance + copy, not colour alone.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  pendingLabel,
  isPending = false,
  confirmDisabled = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descId = useId();
  useFocusTrap(dialogRef, open);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => confirmBtnRef.current?.focus(), 0);
    function handleKeyDown(e: KeyboardEvent) {
      // Don't allow dismissal while a destructive mutation is in flight.
      if (e.key === 'Escape' && !isPending) onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, isPending]);

  if (!open) return null;

  return (
    <div
      className="dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="projects-dialog"
        style={{ maxWidth: 460 }}
      >
        <h2 id={titleId} className="dialog-title">
          {title}
        </h2>
        <div className="dialog-body">
          <div
            id={descId}
            style={{ color: 'var(--fg2)', fontSize: 'var(--fs-14)', lineHeight: 1.5 }}
          >
            {message}
          </div>
        </div>
        <div className="dialog-footer">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            className="btn"
            onClick={onConfirm}
            disabled={isPending || confirmDisabled}
            aria-busy={isPending}
            style={{
              background: 'var(--ut-bluebonnet)',
              borderColor: 'var(--ut-bluebonnet)',
              color: 'var(--ut-white, #fff)',
            }}
          >
            {isPending ? (pendingLabel ?? 'Working…') : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
